import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { usePartnerLockedFundingSources } from './use-partner-locked-funding-sources';

import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  fetchClientByClientIdRequest,
} from '@client/model';
import { clientKeys } from '@client/api/waterlily';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { SessionType } from '@shared';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import type { AllocationOption } from '@/features/guided-financial-planning/components/partner-allocation-section';
import {
  splitFundingSourceFields,
  computeCombinedTotals,
  findPartnerLinkedSource,
  isPartnerSharedRecipient,
} from '@/components/funding-sources-coordination/partner-funding-utils';

export const DEFAULT_CUSTOM_CLIENT_PERCENT = 50;

export type AllocationAction =
  | 'keep'
  | 'mirror'
  | 'split-50-50'
  | 'split-custom';

export interface SplitRatioConfig {
  clientRatio: number;
  partnerRatio: number;
  label: string;
  description: string;
}

interface PersistedSelection {
  action: string;
  partnerFundingSourceId?: string;
  fundingSourceGroupId?: string;
  originalValue: number;
  originalMonetaryValues?: Record<string, number>;
  customClientPercent?: number;
}

export const SPLIT_RATIO_CONFIGS: Record<Exclude<AllocationAction, 'keep' | 'split-custom'>, SplitRatioConfig> = {
  'mirror': {
    clientRatio: 1,
    partnerRatio: 1,
    label: 'Full Each',
    description: 'Both you and your partner each have the full amount available',
  },
  'split-50-50': {
    clientRatio: 0.5,
    partnerRatio: 0.5,
    label: '50 / 50',
    description: 'Split evenly between you and your partner',
  },
};

function allocationOptionToAction(option: AllocationOption): AllocationAction {
  if (option === 'custom') return 'split-custom';
  return option;
}

export function actionToAllocationOption(action: string): AllocationOption {
  if (action === 'split-custom') return 'custom';
  if (action === 'mirror' || action === 'split-50-50') return action;
  // Legacy split ratios (60/40, 70/30, 75/25) map to custom
  if (action.startsWith('split-')) return 'custom';
  return 'mirror';
}

function getRatios(
  action: AllocationAction,
  customClientPercent?: number,
): { clientRatio: number; partnerRatio: number } {
  if (action === 'split-custom') {
    const pct = Math.min(99, Math.max(1, customClientPercent ?? 50));
    return { clientRatio: pct / 100, partnerRatio: (100 - pct) / 100 };
  }
  if (action === 'keep') return { clientRatio: 1, partnerRatio: 0 };
  return SPLIT_RATIO_CONFIGS[action];
}

export function getActionLabel(action: string, customClientPercent?: number): string {
  if (action === 'split-custom' && customClientPercent != null) {
    return `${customClientPercent} / ${100 - customClientPercent}`;
  }
  if (action === 'keep') return 'Keep';
  const config = SPLIT_RATIO_CONFIGS[action as Exclude<AllocationAction, 'keep' | 'split-custom'>];
  return config?.label ?? action;
}

export interface CardAllocationState {
  enabled: boolean;
  option: AllocationOption;
  customClientPercent: number;
  originalValue?: number;
  originalMonetaryValues?: Record<string, number>;
  formValue?: number;
}

export function usePartnerAllocation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();

  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);

  const { session } = useSession();
  const { updateStepData } = useSessionMutations();

  const hasPartner = flags.partnerFundingSplit === true && !!client?.partnerClientId;

  const {
    partnerClient,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
  } = usePartnerLockedFundingSources();

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const savedSelections = useMemo(
    () =>
      (session?.stepData?.partnerAllocationSelections ?? {}) as Record<string, PersistedSelection>,
    [session?.stepData?.partnerAllocationSelections],
  );

  const partnerSelfFunding = partnerClient?.multipleFundingSources?.selfFunding;

  /**
   * Derives allocation state from the source itself (canonical) rather than the
   * session entry. This way modal-created splits are recognized, and stale
   * session entries left over from cross-flow edits are ignored.
   */
  const getCardAllocationState = useCallback(
    (fundingSourceId: string, source?: SelfFundingSource): CardAllocationState => {
      const isMirroredOnSource = source?.partnerAction === 'mirrored';

      if (!isMirroredOnSource) {
        // Source is not mirrored — any session entry is stale. Treat as off.
        return {
          enabled: false,
          option: 'mirror',
          customClientPercent: DEFAULT_CUSTOM_CLIENT_PERCENT,
          originalValue: undefined,
        };
      }

      const partnerSource = source?.fundingSourceGroupId
        ? findPartnerLinkedSource(partnerSelfFunding, source.fundingSourceGroupId)?.[1]
        : undefined;
      const combined = computeCombinedTotals(source, partnerSource);
      const splitRatio = source?.splitRatio ?? null;
      const option: AllocationOption =
        splitRatio == null ? 'mirror' : splitRatio === 50 ? 'split-50-50' : 'custom';

      return {
        enabled: true,
        option,
        customClientPercent: splitRatio ?? DEFAULT_CUSTOM_CLIENT_PERCENT,
        originalValue: combined.existingAssetsValue,
        originalMonetaryValues: combined,
      };
    },
    [partnerSelfFunding],
  );

  const applyCardAllocation = useCallback(
    async (
      fundingSourceId: string,
      assetTypeId: AssetTypeId,
      selfFundingSource: SelfFundingSource,
      allocationState: CardAllocationState,
    ) => {
      if (!client || !partnerClient) return;

      // Look up the partner source and its ID directly from the partner's funding
      // sources (canonical source-of-truth) rather than session metadata. This way
      // modal-initiated splits and guided-planning splits behave identically.
      const groupIdOnSource = selfFundingSource.fundingSourceGroupId ?? null;
      const partnerEntry = groupIdOnSource
        ? findPartnerLinkedSource(partnerSelfFunding, groupIdOnSource)
        : undefined;
      const partnerSource = partnerEntry?.[1];
      const existingPartnerSourceId =
        partnerEntry?.[0] ?? savedSelections[fundingSourceId]?.partnerFundingSourceId ?? null;

      const isCurrentlyMirrored = selfFundingSource.partnerAction === 'mirrored';

      // True pre-split totals derived from current sources (not session storage):
      // mirror sources hold the full amount on each side, split sources hold
      // each partner's portion. Form's combined value, when provided, overrides
      // existingAssetsValue (the user just typed a new total).
      const sourceTotals = computeCombinedTotals(selfFundingSource, partnerSource);
      const trueOriginals: Record<string, number> = {
        ...sourceTotals,
        ...(allocationState.formValue != null && {
          existingAssetsValue: allocationState.formValue,
        }),
      };
      const originalValue =
        trueOriginals.existingAssetsValue ?? selfFundingSource.existingAssetsValue ?? 0;

      const updatedSelections: Record<string, PersistedSelection> = { ...savedSelections };

      // === UNLINK: user unchecked the allocation checkbox ===
      if (!allocationState.enabled) {
        if (isCurrentlyMirrored) {
          // Unlink result is identical regardless of which side initiates: the
          // originator (owner) ends up with the combined total; the other copy
          // is deleted. For a mirror, no totals change — both sides already hold
          // the full amount.
          const wasSplit = selfFundingSource.splitRatio != null;
          const ownerRestore: Partial<SelfFundingSource> = {};

          if (wasSplit) {
            ownerRestore.existingAssetsValue = trueOriginals.existingAssetsValue;
            if (trueOriginals.monthlyContributionAmount != null) {
              ownerRestore.monthlyContributionAmount = trueOriginals.monthlyContributionAmount;
            }
            if (trueOriginals.monthlyIncome != null) {
              ownerRestore.monthlyIncome = trueOriginals.monthlyIncome;
            }
            if (trueOriginals.drawdownAmount != null) {
              ownerRestore.drawdownAmount = trueOriginals.drawdownAmount;
            }
          }

          const currentIsRecipient =
            !!partnerSource && isPartnerSharedRecipient(selfFundingSource, client.clientId);

          if (currentIsRecipient && partnerSource && existingPartnerSourceId) {
            // Restore the owner (partner) side, delete this (recipient) side.
            await guidedPlanningApi.upsertSelfFunding({
              clientId: partnerClient.clientId,
              fundingSourceId: existingPartnerSourceId,
              fundingSourceData: {
                ...partnerSource,
                ...ownerRestore,
                hasSelfFunding: true,
                partnerAction: null,
                fundingSourceGroupId: null,
                splitRatio: null,
                addedByClientId: null,
              },
            });
            await guidedPlanningApi.upsertSelfFunding({
              clientId: client.clientId,
              fundingSourceId,
              fundingSourceData: null,
            });
          } else {
            // Current is owner — restore current, delete partner copy.
            await guidedPlanningApi.upsertSelfFunding({
              clientId: client.clientId,
              fundingSourceId,
              fundingSourceData: {
                ...selfFundingSource,
                ...ownerRestore,
                partnerAction: null,
                fundingSourceGroupId: null,
                splitRatio: null,
                addedByClientId: null,
              },
            });
            if (existingPartnerSourceId) {
              await guidedPlanningApi.upsertSelfFunding({
                clientId: partnerClient.clientId,
                fundingSourceId: existingPartnerSourceId,
                fundingSourceData: null,
              });
            }
          }
        }

        // Always clear any session entry — including stale ones from a source
        // that was unlinked elsewhere.
        delete updatedSelections[fundingSourceId];
        await updateStepData({ partnerAllocationSelections: updatedSelections });
        dispatch(fetchClientByClientIdRequest(client.clientId));
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(partnerClient.clientId) });
        return;
      }

      // === LINK / UPDATE: user enabled allocation or changed option ===
      const action = allocationOptionToAction(allocationState.option);
      const { clientRatio } = getRatios(action, allocationState.customClientPercent);
      const isMirror = action === 'mirror';

      const groupId = groupIdOnSource ?? crypto.randomUUID();
      const clientSplitRatio = isMirror ? null : Math.round(clientRatio * 100);
      const partnerSplitRatio = isMirror ? null : (100 - Math.round(clientRatio * 100));

      // Build source with the true pre-split totals so split/mirror always operates
      // on the original amounts (never on already-split values).
      const sourceWithTotals: SelfFundingSource = {
        ...selfFundingSource,
        ...trueOriginals,
      };

      const { clientOverrides, partnerOverrides } = isMirror
        ? {
            // Mirror: each partner gets the full original amount for every monetary field.
            clientOverrides: { ...trueOriginals },
            partnerOverrides: { ...trueOriginals },
          }
        : splitFundingSourceFields(sourceWithTotals, Math.round(clientRatio * 100));

      // Stamp the originator on both sides so the recipient can be locked to
      // read-only without depending on session metadata. Preserve the existing
      // originator on re-saves so changing a ratio doesn't transfer ownership.
      const addedByClientId = selfFundingSource.addedByClientId ?? client.clientId;

      await guidedPlanningApi.upsertSelfFunding({
        clientId: client.clientId,
        fundingSourceId,
        fundingSourceData: {
          ...selfFundingSource,
          ...clientOverrides,
          partnerAction: 'mirrored',
          fundingSourceGroupId: groupId,
          splitRatio: clientSplitRatio,
          addedByClientId,
        },
      });

      const partnerResponse = await guidedPlanningApi.upsertSelfFunding({
        clientId: partnerClient.clientId,
        fundingSourceId: existingPartnerSourceId,
        fundingSourceData: {
          ...(partnerSource ?? selfFundingSource),
          ...partnerOverrides,
          hasSelfFunding: true,
          partnerAction: 'mirrored',
          fundingSourceGroupId: groupId,
          splitRatio: partnerSplitRatio,
          addedByClientId,
          ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
        },
      });

      updatedSelections[fundingSourceId] = {
        action,
        originalValue,
        originalMonetaryValues: trueOriginals,
        partnerFundingSourceId: partnerResponse.fundingSourceId,
        fundingSourceGroupId: groupId,
        ...(action === 'split-custom' && {
          customClientPercent: allocationState.customClientPercent,
        }),
      };

      // Persist selections to session
      await updateStepData({ partnerAllocationSelections: updatedSelections });

      // Refresh Redux state for the current client and invalidate the partner's
      // React Query cache so combined totals reflect the new partner-side write.
      dispatch(fetchClientByClientIdRequest(client.clientId));
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(partnerClient.clientId) });
    },
    [
      client,
      partnerClient,
      partnerSelfFunding,
      savedSelections,
      isAdvisorSession,
      advisorId,
      dispatch,
      updateStepData,
      queryClient,
    ],
  );

  return {
    hasPartner,
    client,
    partnerClient,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    getCardAllocationState,
    applyCardAllocation,
  };
}
